import { formatAccountLabel, type SettleCreancesData, toUTCMidnight } from '@cpce/shared';
import type { Decimal } from 'decimal.js';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useSettleCreances } from '@/api/prestation-transactions/prestation-transactions.mutations';
import { ConfirmModal } from '@/components/ConfirmModal';
import { DatePicker } from '@/components/DatePicker';
import { FieldLabel } from '@/components/form/FieldLabel';
import { InfoCard } from '@/components/InfoCard';
import { Input } from '@/components/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/Select';
import { formatDecimalAsPrice } from '@/utils/string.utils';

type CreanceInstrument = SettleCreancesData['instrument'];

const INSTRUMENT_LABELS: Record<CreanceInstrument, string> = {
  bankTransfer: 'Virement',
  check: 'Chèque',
  bankCard: 'Carte bancaire',
};

type DisbursementAccount = { code: string; name: string | null; producesTransferFile: boolean };

interface SettleCreancesModalProps {
  closeModal: () => void;
  participationIds: number[];
  /** Amount the CSE pays out, as a positive figure. */
  total: Decimal;
  /** The disbursement account of each settled dossier's prestation. */
  disbursementAccounts: (DisbursementAccount | null)[];
}

export function SettleCreancesModal(props: SettleCreancesModalProps) {
  const { closeModal, participationIds, total, disbursementAccounts } = props;
  const settleCreances = useSettleCreances();
  const [date, setDate] = useState<Date>(() => toUTCMidnight(new Date()));
  const [instrument, setInstrument] = useState<CreanceInstrument>('bankTransfer');
  const [instrumentNumber, setInstrumentNumber] = useState('');

  const needsNumber = instrument === 'check';
  const isValid = !needsNumber || instrumentNumber.trim().length > 0;
  const count = participationIds.length;
  const accounts = [
    ...new Map(
      disbursementAccounts.flatMap((account) => (account ? [[account.code, account]] : []))
    ).values(),
  ];
  const accountLabel = accounts.length === 1 ? ` (${formatAccountLabel(accounts[0])})` : '';
  // Only a « Virements à émettre » account queues the virement for a remise and its SEPA file; any
  // other one books it as already sent, which is right when the CSE pays from its bank's website.
  const directAccounts = accounts.filter((account) => !account.producesTransferFile);

  return (
    <ConfirmModal
      title={count > 1 ? 'Régler des créances' : 'Régler la créance'}
      info={{
        title: 'Versement aux bénéficiaires',
        text: `${count > 1 ? `Les ${count} créances sélectionnées seront réglées` : 'La créance sera réglée'} depuis le compte de décaissement de la prestation${accountLabel} : une écriture et un règlement par dossier.`,
      }}
      confirmLabel="Confirmer le règlement"
      disabled={!isValid || count === 0}
      onConfirm={async () => {
        await settleCreances.mutate({
          participationIds,
          date,
          instrument,
          instrumentNumber: needsNumber ? instrumentNumber.trim() : undefined,
        });
      }}
      closeModal={closeModal}
    >
      <div className="text-gray-900 font-bold text-right text-2xl">
        Total versé : <span className="text-green-700">{formatDecimalAsPrice(total)}</span>
      </div>

      <FieldLabel label="Date du règlement">
        <DatePicker
          className="w-full"
          value={date}
          onChange={(value) => value && setDate(toUTCMidnight(value))}
        />
      </FieldLabel>

      <FieldLabel label="Moyen de paiement">
        <Select
          value={instrument}
          onValueChange={(value) => setInstrument(value as CreanceInstrument)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(INSTRUMENT_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldLabel>

      {instrument === 'bankTransfer' && directAccounts.length > 0 && (
        <InfoCard title="Pas de fichier SEPA" color="orange" icon={TriangleAlert}>
          <p className="text-sm text-gray-700">
            {directAccounts.map(formatAccountLabel).join(', ')} n'est pas un compte « Virements à
            émettre » : le virement sera enregistré comme déjà émis et n'apparaîtra pas dans «
            Décaissements à remettre ». Pour le passer dans une remise SEPA, choisissez un compte «
            Virements à émettre » comme compte de décaissement de la prestation.
          </p>
        </InfoCard>
      )}

      {needsNumber && (
        <FieldLabel label="Numéro de chèque">
          <Input
            value={instrumentNumber}
            onChange={(e) => setInstrumentNumber(e.target.value)}
            placeholder="Saisir le numéro"
          />
        </FieldLabel>
      )}
    </ConfirmModal>
  );
}
